/**
 * Results UI Layer
 * Renders the product grid + cards reactively from Tagalys state.
 *
 * The card markup mirrors snippets/product-card.liquid so the existing CSS
 * (assets/product-grid.css) styles it and the registered <product-card> custom
 * element (assets/app.js) upgrades each card — wiring quick-view and interactions
 * with no per-card script.
 */

class ResultsUICollection {
	constructor () {
		this.eventBus = window.theme?.tagalys?.results?.eventBus
		this.config = window.theme?.resultsConfig || {}
		this.card = this.config.card || {}
		this.grid = document.querySelector('#product-grid')
		this.promos = JSON.parse(document.querySelector('[data-promos]')?.textContent || '[]')

		if (!this.eventBus || !this.grid) return

		this.skeletonCount = this.config.productsPerPage || 12

		this.eventBus.subscribe('results:state:loading', () => this.showSkeletons())
		this.eventBus.subscribe('results:state:products:updated', (products) => this.renderProducts(products))

		const existing = window.theme.tagalys.results.stateLayer?.state?.products

		if (Array.isArray(existing) && existing.length) {
			this.renderProducts(existing)
		}
	}

	showSkeletons () {
		const cards = new Array(this.skeletonCount).fill(this.renderSkeletonCard()).join('')

		if (window.theme.tagalys.results.stateLayer.state.pagination.page && window.theme.tagalys.results.stateLayer.state.pagination.page >= 1) {
			this.grid.insertAdjacentHTML('beforeend', cards)
		} else {
			this.grid.innerHTML = cards
		}
	}

	async renderProducts (products) {
		if (!Array.isArray(products)) return

		if (window.theme.tagalys.results.stateLayer.state.pagination && window.theme.tagalys.results.stateLayer.state.pagination.page === 1) {
			let renderPromos = true

			const cards = await Promise.all(products.map((product, index) => this.renderCard(product, index)))
			this.grid.innerHTML = cards.join('')

			const requestType = window.theme.tagalys.results.SDK.helpers.getRequestParams()?.action

			if (requestType === 'APPLY_FILTER') {
				renderPromos = false
			}

			if (renderPromos) {
				this.insertPromos(this.promos)
			}
		} else {
			const skeletons = this.grid.querySelectorAll('.product-card--skeleton')

			if (skeletons?.length) {
				await Promise.all(products.map(async (product, index) => {
					if (skeletons[index]) {
						const temp = document.createElement('div')
						temp.innerHTML = await this.renderCard(product, index)
						skeletons[index].replaceWith(temp.firstElementChild)
					}
				}))

				// Remove any remaining skeleton cards that weren't replaced
				skeletons.forEach((skeleton, index) => {
					if (index >= products.length && skeleton.isConnected) {
						skeleton.remove()
					}
				})
			}
		}

		const resultsCountEl = document.querySelector('[data-tagalys-search-results-count]')

		if (resultsCountEl) {
			const count = window.theme.tagalys.results.stateLayer.state.pagination.total || 0
			const searchTerm = window.theme.tagalys.results.stateLayer.state.searchParams.split('=')[1]?.split('&')[0]

			resultsCountEl.textContent = `${count} results found for "${decodeURIComponent(searchTerm || '')}"`
		}

		this.setupSwatchEvents()
	}

	setupSwatchEvents () {
		const productCards = this.grid.querySelectorAll('product-card')
		const supportsHover = window.matchMedia('(hover: hover)').matches

		productCards.forEach((productCard) => {
			const productCardLink = productCard.querySelector('.product-featured-image-link')
			const productImages = productCard.querySelectorAll('.product-variant-image')
			const swatches = productCard.querySelectorAll('.color-swatch')

			if (!swatches.length || !productCardLink) return

			const setSwatchColorVisible = (variantId) => {
				if (!supportsHover) return

				productImages.forEach((image) => {
					image.classList.toggle('visible', image.getAttribute('id') === variantId)
				})
			}

			const setSwatchColorActive = (swatch, variantId, variantUrl) => {
				swatch.classList.add('swatch--active')

				swatches.forEach((otherSwatch) => {
					if (otherSwatch !== swatch) {
						otherSwatch.classList.remove('swatch--active')
					}
				})

				productCardLink.href = variantUrl
				if (variantId.toString() === productCard.id.replace('product-card-', '')) {
					productImages.forEach((image) => {
						image.classList.remove('active')
					})
				} else {
					const secondaryImage = productCard.querySelector('.product-secondary-image')
					if (secondaryImage) {
						secondaryImage.classList.remove('hover')
					}

					productImages.forEach((image) => {
						image.classList.toggle('active', image.getAttribute('id') === variantId)
					})
				}

				const quickViewButton = productCard.querySelector('.product-card-quickview')
				if (quickViewButton) {
					quickViewButton.setAttribute('data-product-handle', variantUrl.split(`${Shopify.routes.root}products/`)[1])
				}
			}

			swatches.forEach((swatch) => {
				const variantId = swatch.getAttribute('data-variant-id')
				const variantUrl = swatch.getAttribute('data-variant-url')

				swatch.addEventListener('click', () => setSwatchColorActive(swatch, variantId, variantUrl))
				swatch.addEventListener('mouseover', () => setSwatchColorVisible(variantId))
				swatch.addEventListener('mouseout', () => {
					if (supportsHover) {
						productImages.forEach((image) => {
							image.classList.remove('visible')
						})
					}
				})
			})
		})
	}

	insertPromos (promos) {
		// const items = this.grid.children
		promos.forEach((promo) => {
			const indexDesktop3Col = promo.index_desktop_3_column
			const indexDesktop4Col = promo.index_desktop_4_column
			const indexMobile = promo.index_mobile
			if (indexDesktop3Col == null || indexDesktop4Col == null || indexMobile == null) return

			const html = this.renderPromos([promo])
			if (!html) return

			const temp = document.createElement('div')
			temp.innerHTML = html

			const promoEl = temp.firstElementChild

			this.grid.appendChild(promoEl)
		})
	}

	/* ---------- Skeleton ---------- */

	renderSkeletonCard () {
		return `
			<li class="column">
				<div class="product-card product-card--skeleton text-${this.card.contentAlignment || 'center'}">
					<figure class="product-featured-image">
						<span class="product-featured-image-link aspect-ratio aspect-ratio--${this.card.aspectRatio || 'adapt'} skeleton-shimmer"></span>
					</figure>

					<div class="product-card-info">
						<div class="product-title-price">
							<span class="skeleton-bar skeleton-bar--title skeleton-shimmer"></span>
							<span class="skeleton-bar skeleton-bar--price skeleton-shimmer"></span>
						</div>
					</div>
				</div>
			</li>
		`
	}

    /* ---------- Product card ---------- */

	async renderCard (product, index) {
		const align = this.card.contentAlignment || 'center'
		const title = this.escape(product.title)
		const figure = await this.renderFigure(product, index)

		return `
			<li class="column">
				<product-card class="product-card text-${align}" id="product-card-${product.id}">
					${figure}

					<div class="product-card-info">
						<div class="product-title-price">
							<a href="${this.productUrl(product)}" title="${title}" class="product-card-title fz-14_100">
								${title}
							</a>

							${this.renderPrice(product)}
						</div>

						${this.renderBadges(product)}
					</div>
				</product-card>
			</li>
		`
	}

	async renderFigure (product, index) {
		const images = product.images || []
		const featured = product.featured_image || images[0]
		const hoverEnabled = this.card.hoverImages && images.length > 1
		const hoverCount = this.card.hoverImagesCount || 1

		let hoverImgs = ''
		let dots = ''
		if (hoverEnabled) {
			for (let i = 1; i <= hoverCount; i++) {
				if (images[i]) hoverImgs += await this.renderImage(images[i], null, 'product-secondary-image', index)
			}

			if (this.card.hoverDots) {
				let items = '<li class="active"></li>'

				for (let i = 1; i <= hoverCount; i++) {
					if (images[i]) items += '<li></li>'
				}
				dots = `<ul class="product-secondary-images-nav">${items}</ul>`
			}
		}

		let variantMedia = ''
		const colour_variants = product.metafields?.custom?.colour_variants?.value || []

		if (Array.isArray(colour_variants)) {
			const variantPromises = colour_variants.map(async variant => {
				const response = await fetch(`${Shopify.routes.root}products/${variant.handle}.js`)
				const variantData = await response.json()

				return variantData
			})

			const variantResults = await Promise.all(variantPromises)

			variantMedia = variantResults.map((variantData, index) => {
				const image = variantData.media?.[0]

				return image ? this.renderImage(image, variantData.id, 'product-variant-image', index) : ''
			}).join('')
		}

		const quickview = this.card.enableQuickView ? this.renderQuickView(product) : ''
		const title = this.escape(product.title)

		return `
			<figure class="product-featured-image${hoverEnabled ? ' thb-hover' : ''}">
				<a href="${this.productUrl(product)}" title="${title}" class="product-featured-image-link aspect-ratio aspect-ratio--${this.card.aspectRatio || 'adapt'}">
					${variantMedia}
					${hoverImgs}
					${featured ? await this.renderImage(featured, null, 'product-primary-image', index) : ''}
					${quickview}
					${dots}
				</a>

				${this.renderSwatches(product)}
			</figure>
		`
	}

	renderImage (image, id, className, index) {
		const src = image.src
		const w = image.width || ''
		const h = image.height || ''
		const alt = this.escape(image.alt || '')
		const srcset = `${this.cdn(src, 298)} 298w, ${this.cdn(src, 596)} 596w`

		if (index < 3 && className === 'product-primary-image') {
			return `<img${id != null ? ` id="${id}"` : ''} class="${className}" width="${w}" height="${h}" sizes="auto" src="${this.cdn(src, 298)}" srcset="${srcset}" alt="${alt}">`
		}
		return `<img${id != null ? ` id="${id}"` : ''} class="lazyload ${className}" width="${w}" height="${h}" data-sizes="(min-width: 768px) 50vw, 100vw" src="${this.cdn(src, 20)}" data-srcset="${srcset}" alt="${alt}">`
	}

	cdn (src, width) {
		if (!src) return ''
		return `${src}${src.includes('?') ? '&' : '?'}width=${width}`
	}

	/* ---------- Price & badges ---------- */

	renderPrice (product) {
		const onSale = product.compare_at_price != null && product.compare_at_price > product.price
		const compare = onSale
			? `<del><span class="amount">${this.money(product.compare_at_price)}</span></del>`
			: ''
		const priceText = product.price_varies
			? `From ${this.money(product.price_min)}`
			: this.money(product.price)

		return `<span class="price">${compare}<ins><span class="amount">${priceText}</span></ins></span>`
	}

	money (amount) {
		const cents = Math.round((amount || 0) * 100)
		const fmt = this.card.moneyFormat || '${{amount}}'

		if (typeof window.formatMoney === 'function') {
			return window.formatMoney(cents, fmt)
		}
		return formatMoneyShopify(cents, fmt)
	}

	metafieldValue (metafield) {
		if (metafield == null) return ''
		if (typeof metafield === 'string' || typeof metafield === 'number') return String(metafield)
		if (metafield.value != null) return String(metafield.value)
		return ''
	}

	soldOutBadgeLabel (product) {
		const country = window.Shopify?.country || 'GB'
		const custom = product.metafields?.custom || {}
		const badgeUs = this.metafieldValue(custom.product_badge_us)
		const badgeUk = this.metafieldValue(custom.product_badge_uk)

		if (country === 'US' && badgeUs) return badgeUs
		if (country === 'GB' && badgeUk) return badgeUk

		return ''
	}

	renderBadges (product) {
		let badges = ''

		if (product.available === false) {
			const soldOutOverride = this.soldOutBadgeLabel(product)

			if (soldOutOverride) {
				// Match PDP: custom sold-out labels (e.g. COMING SOON) replace the default badge
				badges = `<span class="badge fz-11_100">${this.escape(soldOutOverride)}</span>`
			} else {
				badges = `<span class="badge out-of-stock fz-11_100">${this.escape(this.card.soldOutLabel || 'Sold out')}</span>`
			}
		} else if (product.compare_at_price != null && product.compare_at_price > product.price) {
			badges = `<span class="badge onsale fz-11_100">${this.saleBadge(product)}</span>`
		}

		if (product.metafields.custom?.product_label?.value) {
			badges += `
				<span class="badge fz-11_100">
					${this.escape(product.metafields.custom.product_label.value)}
				</span>`
		}

		return `<div class="badge-container">${badges}</div>`
	}

	saleBadge (product) {
		const type = this.card.saleBadgeType

		if (type === 'save_amount') {
			return this.money(product.compare_at_price - product.price)
		}

		if (type === 'save_percentage') {
			const pct = Math.round((product.compare_at_price - product.price) * 100 / product.compare_at_price)
			return `${pct}%`
		}

		return 'Sale'
	}

	renderQuickView (product) {
		return `
			<button class="product-card-quickview" href="#Product-Drawer" data-product-handle="${this.escape(product.handle)}" tabindex="-1">
				<div class="loading-overlay">
					<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
						<circle class="spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="var(--Color_CobaltBlue)"></circle>
					</svg>
				</div>

				<span class="plus-icon">
					<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M3.6 0H4.4V8H3.6V0Z" fill="var(--Color_CobaltBlue)"></path>
						<path d="M3.49692e-08 4.4L0 3.6L8 3.6V4.4L3.49692e-08 4.4Z" fill="var(--Color_CobaltBlue)"></path>
					</svg>
				</span>
			</button>
		`
	}

	renderSwatches (product) {
		const swatches = product.metafields?.custom?.colour_variants?.value || []

		if (!Array.isArray(swatches) || !swatches.length) return ''

		return `
			<div id="colorSwatches" class="${this.card.hideSwatches ? 'hide' : ''}">
				${swatches.map((swatch, index) => {
					const link = swatch.handle
					const color = swatch.metafields?.custom?.colour_variant?.value
					const label = this.escape(swatch.metafields?.custom?.colour?.value)

					return `
						<span
							class="${index === 0 ? 'first-swatch ' : ''}color-swatch${product.id === swatch.id ? ' swatch--active' : ''}"
							data-variant-id="${swatch.id}"
							data-variant-url="${Shopify.routes.root}products/${link}"
							${color ? `style="background-color: ${color}"` : ''}
						>
							<span class="swatch-tooltip">${label}</span>
						</span>
					`
				}).join('')}
			</div>
		`
	}

	/* ---------- Promos ---------- */

	renderPromos (promos) {
		return promos.map((promo) => {
			const indexMobile = promo.index_mobile
			const indexDesktop3Col = promo.index_desktop_3_column
			const indexDesktop4Col = promo.index_desktop_4_column
			const spanRow = parseInt(promo.size.split('x')[0], 10)
			const spanCol = parseInt(promo.size.split('x')[1], 10)

			const mobileCol = indexMobile % 2
			const desktop3ColCol = indexDesktop3Col % 3
			const desktop4ColCol = indexDesktop4Col % 4

			const mobileRow = Math.floor((indexMobile - 1) / 2) * 2 + 1
			const desktop3ColRow = Math.floor((indexDesktop3Col - 1) / 3) * 2 + 1
			const desktop4ColRow = Math.floor((indexDesktop4Col - 1) / 4) * 2 + 1

			const title = this.escape(promo.heading || '')
			const text = this.escape(promo.description || '')
			const blockLink = promo.button_link || ''

			console.log(blockLink)

			const products = this.renderQuickViewProducts(promo.products)

			const mediaType = promo.media?.type || ''
			const mediaAlt = promo.media?.alt || ''
			let videoTag = ''
			let imgTag = ''
			if (mediaType === 'video') {
				const mediaSources = promo.media?.sources || []
				const mediaPreviewImage = promo.media?.preview_image || ''
				videoTag = this.renderPromoVideo(mediaSources, mediaPreviewImage, mediaAlt)
			} else {
				const image = promo.media?.image
				imgTag = this.renderPromoImage(image)
			}

			const titleHtml = title ? `
				<p class="promotion-card-title fz-14_100"">
					${title}
				</p>` : ''
			const textHtml = text ? `
				<p class="promotion-card-description">
					${text}
				</p>` : ''

			return `
				<li
					class="column column-promo"
					data-size="${spanRow}x${spanCol}"
					style="
						--SpanRow: ${spanRow};
						--SpanCol: ${spanCol};
						--MobileRow: ${mobileRow};
						--Desktop3ColRow: ${desktop3ColRow};
						--Desktop4ColRow: ${desktop4ColRow};
						--MobileCol: ${mobileCol};
						--Desktop3ColCol: ${desktop3ColCol};
						--Desktop4ColCol: ${desktop4ColCol};
					"
				>
					<div class="promotion-card product-card">
						<figure class="product-featured-image">
							<${blockLink !== '' ? 'a' : 'div'}${blockLink !== '' ? ` href="${blockLink}"` : ''} class="product-featured-image-link aspect-ratio aspect-ratio--1x1">
								${mediaType === 'video' ? videoTag : imgTag}
							</${blockLink !== '' ? 'a' : 'div'}>

							${products}
						</figure>

						<div class="product-card-info">
							<a href="${blockLink}">
								${titleHtml}
								${textHtml}
							</a>
						</div>
					</div>
				</li>
			`
		}).join('')
	}

	renderQuickViewProducts (products) {
		return products.map(product => `
			<div class="promotion-card-products">
				<pinned-products-quick-add
					data-product-handle="${product.handle}"
					data-variant-id="${product.variant_id}"
				>
					<button
						type="button"
						class="mod-PinnedProduct_ItemButton"
						aria-label="Quick add ${product.title}"
						aria-controls="Product-Drawer"
						aria-haspopup="dialog"
						aria-expanded="false"
					>
						${this.renderPromoQuickViewProductImage(product.image)}
					</button>
				</pinned-products-quick-add>
			</div>
		`).join('')
	}

	renderPromoVideo (sources, previewImage, alt) {
		const sourceTags = sources.map(source => `<source src="${source.url}" type="${source.mime_type}">`).join('')
		return `
			<video
				class="plp-promo-video"
				autoplay
				muted
				loop
				playsinline
				poster="${previewImage}"
				alt="${alt}"
			>
				${sourceTags}
			</video>
		`
	}

	renderPromoImage (image) {
		const src = image.src || ''
		const w = image.width || ''
		const h = image.height || ''
		const alt = this.escape(image.alt || '')
		const srcset = `${this.cdn(src, 298)} 298w, ${this.cdn(src, 596)} 596w, ${this.cdn(src, 894)} 894w, ${this.cdn(src, 1192)} 1192w, ${this.cdn(src, 1488)} 1488w, ${this.cdn(src, 1784)} 1784w`

		return `<img class="lazyload" width="${w}" height="${h}" data-sizes="(min-width: 768px) 50vw, 100vw" src="${this.cdn(src, 20)}" data-srcset="${srcset}" alt="${alt}">`
	}

	renderPromoQuickViewProductImage (image) {
		const src = image.src || ''
		const alt = this.escape(image.alt || '')
		const srcset = `${this.cdn(src, 298)} 154w, ${this.cdn(src, 298)} 298w`

		return `<img class="lazyload" width="106" height="160" data-sizes="100vw" src="${this.cdn(src, 20)}" data-srcset="${srcset}" alt="${alt}">`
	}

	/* ---------- Helpers ---------- */

	productUrl (product) {
		const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '')

		return `${root}/products/${product.handle}`
	}

	escape (str) {
		if (str == null) return ''

		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
	}

	allPromosData () {
    if (!this._allPromosData) {
      const promoDataEl = document.querySelector('[data-promos]')
      try {
        this._allPromosData = promoDataEl
          ? JSON.parse(
            promoDataEl.textContent.trim().replace(/,(\s*[\]}])/g, '$1'),
          )
          : []
      } catch (e) {
        console.error('Failed to parse promo data', e)
        this._allPromosData = []
      }
    }
    return this._allPromosData
  }
}

/**
 * Standalone fallback of Shopify's money formatter, used only if the theme's
 * global formatMoney isn't available at render time.
 */
function formatMoneyShopify (cents, format) {
	if (typeof cents === 'string') cents = cents.replace('.', '')
	const placeholder = /\{\{\s*(\w+)\s*\}\}/

	function withDelimiters (number, precision, thousands, decimal) {
		precision = precision == null ? 2 : precision
		thousands = thousands == null ? ',' : thousands
		decimal = decimal == null ? '.' : decimal
		if (isNaN(number) || number == null) return '0'
		number = (number / 100.0).toFixed(precision)
		const parts = number.split('.')
		const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
		const centsPart = parts[1] ? decimal + parts[1] : ''
		return dollars + centsPart
	}

	let value = ''
	const match = (format || '').match(placeholder)
	switch (match ? match[1] : 'amount') {
		case 'amount': value = withDelimiters(cents, 2); break
		case 'amount_no_decimals': value = withDelimiters(cents, 0); break
		case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break
		case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break
		case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break
		case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break
		case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break
		default: value = withDelimiters(cents, 2)
	}

	return (format || '').replace(placeholder, value)
}

function initResultsUICollection () {
  window.theme.tagalys.results.uiLayer = new ResultsUICollection()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initResultsUICollection)
} else {
  initResultsUICollection()
}
